import {
  BankTypesEnum,
  BankTypesEnumAPI,
  type Contract,
  type ContractAPI,
  type ContractOutstandingFee,
  type ContractOutstandingFeeApi,
  type Contractor,
  type ContractorAPI,
  type ContractorIdentityVerification,
  type ContractorIdentityVerificationAPI,
  type ContractorPersonalDetails,
  type ContractorPersonalDetailsAPI,
  type CreateContractorRequest,
  type CreateContractorRequestAPI,
  type GetContractorAllowedEntitiesResponse,
  type GetContractorAllowedEntitiesResponseAPI,
  type GetContractorBankDetailsResponse,
  type GetContractorBankDetailsResponseAPI,
  type GetContractorsCostsRequest,
  type GetContractorsCostsRequestAPI,
  type GetContractorsCostsResponse,
  type GetContractorsCostsResponseAPI,
  type GetContractorsResponse,
  type GetContractorsResponseAPI,
  type LiabilityShield,
  type LiabilityShieldAPI,
  LiabilityShieldState,
  LiabilityShieldStateAPI,
  type UpdateContractorContractRequest,
  type UpdateContractorContractRequestAPI,
  type UpdateContractorIdentityVerificationRequest,
  type UpdateContractorIdentityVerificationRequestAPI,
  type UpdateContractorPersonalDetailsRequest,
  type UpdateContractorPersonalDetailsRequestAPI,
} from '@omnipresentgroup/types';

const transformLiabilityShieldStateFromAPI = (
  state: LiabilityShieldStateAPI,
): LiabilityShieldState => {
  const mapping: Record<LiabilityShieldStateAPI, LiabilityShieldState> = {
    [LiabilityShieldStateAPI.ACTIVE]: LiabilityShieldState.ACTIVE,
    [LiabilityShieldStateAPI.INACTIVE]: LiabilityShieldState.INACTIVE,
    [LiabilityShieldStateAPI.AWAITING_ACTIVATION]:
      LiabilityShieldState.AWAITING_ACTIVATION,
  };
  return mapping[state];
};

const transformLiabilityShieldFromAPI = ({
  state,
  start_date,
  end_date,
  created_at,
  updated_at,
}: LiabilityShieldAPI): LiabilityShield => ({
  state: transformLiabilityShieldStateFromAPI(state),
  startDate: start_date,
  endDate: end_date,
  createdAt: created_at,
  updatedAt: updated_at,
});

export const transformContractFromAPI = ({
  id,
  contractor_id,
  client_id,
  job_role,
  invoicing_currency,
  billing_currency,
  expected_invoicing_frequency,
  rate,
  start_date,
  end_date,
  state,
  liability_shield_coverages,
}: ContractAPI): Contract => ({
  id,
  contractorId: contractor_id,
  clientId: client_id,
  jobRole: job_role,
  invoicingCurrency: invoicing_currency,
  billingCurrency: billing_currency,
  expectedInvoicingFrequency: expected_invoicing_frequency,
  rate,
  startDate: start_date,
  endDate: end_date,
  state,
  liabilityShieldCoverages: liability_shield_coverages?.map(
    transformLiabilityShieldFromAPI,
  ),
});

export const transformContractToAPI = ({
  id,
  contractorId,
  clientId,
  jobRole,
  invoicingCurrency,
  billingCurrency,
  expectedInvoicingFrequency,
  rate,
  startDate,
  endDate,
  state,
}: Contract): ContractAPI => ({
  id,
  contractor_id: contractorId,
  client_id: clientId,
  job_role: jobRole,
  invoicing_currency: invoicingCurrency,
  billing_currency: billingCurrency,
  expected_invoicing_frequency: expectedInvoicingFrequency,
  rate,
  start_date: startDate,
  end_date: endDate,
  state,
});

export const transformContractorFromAPI = ({
  id,
  first_name,
  last_name,
  email,
  residence_country,
  state,
  contracts,
  user_id,
  onboarding_states,
}: ContractorAPI): Contractor => ({
  id,
  firstName: first_name,
  lastName: last_name,
  email,
  residenceCountry: { ...residence_country },
  state,
  contracts: contracts.map((contract) => transformContractFromAPI(contract)),
  userId: user_id,
  onboardingStates: {
    kycState: onboarding_states.kyc_state,
    personalState: onboarding_states.personal_state,
    bankingAccountState: onboarding_states.banking_account_state,
  },
});

export const transformContractorToAPI = ({
  id,
  firstName,
  lastName,
  email,
  residenceCountry,
  state,
  contracts,
  userId,
  onboardingStates,
}: Partial<Contractor>): Partial<ContractorAPI> => ({
  id,
  first_name: firstName,
  last_name: lastName,
  email,
  residence_country: residenceCountry && { ...residenceCountry },
  state,
  contracts: contracts?.map((contract) => transformContractToAPI(contract)),
  user_id: userId,
  onboarding_states: onboardingStates && {
    kyc_state: onboardingStates?.kycState,
    personal_state: onboardingStates?.personalState,
    banking_account_state: onboardingStates?.bankingAccountState,
  },
});

export const transformGetContractorsResponseFromAPI = ({
  items,
  total,
  page,
  size,
  pages,
}: GetContractorsResponseAPI): GetContractorsResponse => ({
  items: items.map((item) => transformContractorFromAPI(item)),
  total,
  page,
  size,
  pages,
});

export const transformCreateContractorRequestToAPI = ({
  firstName,
  lastName,
  email,
  jobRole,
  residenceCountry,
  invoicingCurrency,
  billingCurrency,
  expectedInvoicingFrequency,
  invoiceAmount,
  startDate,
  endDate,
  misclassificationRiskAccepted,
  misclassificationAssessmentCode,
  contractTemplateUsageAccepted,
}: CreateContractorRequest): CreateContractorRequestAPI => {
  return {
    first_name: firstName,
    last_name: lastName,
    email,
    job_role: jobRole,
    residence_country: residenceCountry,
    invoicing_currency: invoicingCurrency,
    billing_currency: billingCurrency,
    expected_invoicing_frequency: expectedInvoicingFrequency,
    invoice_amount: invoiceAmount,
    start_date: startDate,
    end_date: endDate,
    misclassification_risk_accepted: misclassificationRiskAccepted,
    misclassification_assessment_code: misclassificationAssessmentCode,
    contract_template_usage_accepted: contractTemplateUsageAccepted,
  };
};

export const transformGetContractorsCostsRequestToAPI = ({
  billingCurrency,
  invoicingCurrency,
}: GetContractorsCostsRequest): GetContractorsCostsRequestAPI => {
  return {
    billing_currency: billingCurrency,
    invoicing_currency: invoicingCurrency,
  };
};

export const transformGetContractorsCostsResponseFromAPI = ({
  monthly_platform_fee,
  total_cost,
  vat_rate,
  country,
}: GetContractorsCostsResponseAPI): GetContractorsCostsResponse => {
  return {
    monthlyPlatformFee: monthly_platform_fee,
    totalCost: total_cost,
    vatRate: vat_rate,
    country,
  };
};
export const transformContractorPersonalDetailsFromAPI = ({
  state,
  state_last_error_message,
  first_name,
  last_name,
  residence_country,
  nationality,
  date_of_birth,
}: ContractorPersonalDetailsAPI): ContractorPersonalDetails => {
  return {
    state,
    stateLastErrorMessage: state_last_error_message,
    firstName: first_name,
    lastName: last_name,
    residenceCountry: residence_country,
    nationality,
    dateOfBirth: date_of_birth,
  };
};

export const transformContractorPersonalDetailsToAPI = ({
  firstName,
  lastName,
  residenceCountry,
  nationality,
  dateOfBirth,
}: UpdateContractorPersonalDetailsRequest): UpdateContractorPersonalDetailsRequestAPI => {
  return {
    first_name: firstName,
    last_name: lastName,
    residence_country: residenceCountry,
    nationality,
    date_of_birth: dateOfBirth,
  };
};

const transformBankTypeFromAPI = (
  bankType: BankTypesEnumAPI,
): BankTypesEnum => {
  const mapping: Record<BankTypesEnumAPI, BankTypesEnum> = {
    [BankTypesEnumAPI.IBAN]: BankTypesEnum.IBAN,
    [BankTypesEnumAPI.US]: BankTypesEnum.US,
    [BankTypesEnumAPI.GB]: BankTypesEnum.GB,
    [BankTypesEnumAPI.CA]: BankTypesEnum.CA,
    [BankTypesEnumAPI.AU]: BankTypesEnum.AU,
    [BankTypesEnumAPI.OTHER]: BankTypesEnum.OTHER,
  };
  return mapping[bankType];
};

export const transformContractorBankDetailsFromAPI = (
  contractorBankDetailsAPI: GetContractorBankDetailsResponseAPI,
): GetContractorBankDetailsResponse => {
  return {
    state: contractorBankDetailsAPI.state,
    stateLastErrorMessage: contractorBankDetailsAPI.state_last_error_message,
    bankDetails: {
      type: transformBankTypeFromAPI(
        contractorBankDetailsAPI.bank_account_type,
      ),
      accountHolderName: contractorBankDetailsAPI.owner_name,
      iban: contractorBankDetailsAPI.iban,
      swiftBic: contractorBankDetailsAPI.swift_bic,
      aba: contractorBankDetailsAPI.aba,
      institutionNumber: contractorBankDetailsAPI.institution_number,
      branchCode: contractorBankDetailsAPI.branch_code,
      bankName: contractorBankDetailsAPI.bank_name,
      sortCode: contractorBankDetailsAPI.sort_code,
      bankAccountCountry: contractorBankDetailsAPI.country_code,
      accountNumber: contractorBankDetailsAPI.account_number,
      bankStateBranch: contractorBankDetailsAPI.bank_state_branch,
      id: contractorBankDetailsAPI.id,
    },
    accountHolderAddress: contractorBankDetailsAPI.owner_address && {
      addressLine1: contractorBankDetailsAPI.owner_address.address_line_1,
      addressLine2: contractorBankDetailsAPI.owner_address.address_line_2,
      postalCode: contractorBankDetailsAPI.owner_address.postal_code,
      city: contractorBankDetailsAPI.owner_address.city,
      region: contractorBankDetailsAPI.owner_address.region,
      country: contractorBankDetailsAPI.owner_address.country,
    },
  };
};

const transformBankTypeToAPI = (bankType: BankTypesEnum): BankTypesEnumAPI => {
  const mapping: Record<BankTypesEnum, BankTypesEnumAPI> = {
    [BankTypesEnum.IBAN]: BankTypesEnumAPI.IBAN,
    [BankTypesEnum.US]: BankTypesEnumAPI.US,
    [BankTypesEnum.GB]: BankTypesEnumAPI.GB,
    [BankTypesEnum.CA]: BankTypesEnumAPI.CA,
    [BankTypesEnum.AU]: BankTypesEnumAPI.AU,
    [BankTypesEnum.OTHER]: BankTypesEnumAPI.OTHER,
  };
  return mapping[bankType];
};

export const transformContractorBankDetailsToAPI = (
  contractorBankDetails: GetContractorBankDetailsResponse,
): GetContractorBankDetailsResponseAPI => {
  return {
    state: contractorBankDetails.state,
    bank_account_type: transformBankTypeToAPI(
      contractorBankDetails.bankDetails.type,
    ),
    owner_name: contractorBankDetails.bankDetails.accountHolderName,
    iban: contractorBankDetails.bankDetails.iban,
    swift_bic: contractorBankDetails.bankDetails.swiftBic,
    aba: contractorBankDetails.bankDetails.aba,
    institution_number: contractorBankDetails.bankDetails.institutionNumber,
    branch_code: contractorBankDetails.bankDetails.branchCode,
    bank_name: contractorBankDetails.bankDetails.bankName,
    sort_code: contractorBankDetails.bankDetails.sortCode,
    country_code: contractorBankDetails.bankDetails.bankAccountCountry,
    account_number: contractorBankDetails.bankDetails.accountNumber,
    bank_state_branch: contractorBankDetails.bankDetails.bankStateBranch,
    owner_address: {
      address_line_1: contractorBankDetails.accountHolderAddress.addressLine1,
      address_line_2: contractorBankDetails.accountHolderAddress.addressLine2,
      postal_code: contractorBankDetails.accountHolderAddress.postalCode,
      city: contractorBankDetails.accountHolderAddress.city,
      region: contractorBankDetails.accountHolderAddress.region,
      country: contractorBankDetails.accountHolderAddress.country,
    },
  };
};

export const transformContractorIdentityVerificationFromAPI = ({
  state,
  state_last_error_message,
  entity_type,
  terms_agreed,
  terms_agreed_at,
  entity_type_confirmed,
  entity_type_confirmed_at,
  registered_legal_name,
  entity_email,
  company_number,
  headquarters_address,
  legal_representative,
}: ContractorIdentityVerificationAPI): ContractorIdentityVerification => {
  return {
    state,
    stateLastErrorMessage: state_last_error_message,
    entityType: entity_type,
    termsAgreed: terms_agreed,
    termsAgreedAt: terms_agreed_at,
    entityTypeConfirmed: entity_type_confirmed,
    entityTypeConfirmedAt: entity_type_confirmed_at,
    registeredLegalName: registered_legal_name,
    entityEmail: entity_email,
    companyNumber: company_number,
    headquartersAddress: headquarters_address && {
      addressLine1: headquarters_address.address_line_1,
      addressLine2: headquarters_address.address_line_2,
      city: headquarters_address.city,
      postalCode: headquarters_address.postal_code,
      region: headquarters_address.region,
      country: headquarters_address.country,
    },
    legalRepresentative: legal_representative && {
      firstName: legal_representative.first_name,
      lastName: legal_representative.last_name,
      dateOfBirth: legal_representative.date_of_birth,
      nationality: legal_representative.nationality,
      countryOfResidence: legal_representative.country_of_residence,
    },
  };
};

export const transformContractorIdentityVerificationToAPI = ({
  entityType,
  termsAgreed,
  entityTypeConfirmed,
  registeredLegalName,
  entityEmail,
  companyNumber,
  headquartersAddress,
  legalRepresentative,
}: UpdateContractorIdentityVerificationRequest): UpdateContractorIdentityVerificationRequestAPI => {
  return {
    entity_type: entityType,
    terms_agreed: termsAgreed,
    entity_type_confirmed: entityTypeConfirmed,
    registered_legal_name: registeredLegalName,
    entity_email: entityEmail,
    company_number: companyNumber,
    headquarters_address: headquartersAddress && {
      address_line_1: headquartersAddress.addressLine1,
      address_line_2: headquartersAddress.addressLine2,
      city: headquartersAddress.city,
      postal_code: headquartersAddress.postalCode,
      region: headquartersAddress.region,
      country: headquartersAddress.country,
    },
    legal_representative: legalRepresentative && {
      first_name: legalRepresentative.firstName,
      last_name: legalRepresentative.lastName,
      date_of_birth: legalRepresentative.dateOfBirth,
      nationality: legalRepresentative.nationality,
      country_of_residence: legalRepresentative.countryOfResidence,
    },
  };
};

export const transformContractorAllowedEntityTypesFromAPI = ({
  allowed_kyc_entity_types,
}: GetContractorAllowedEntitiesResponseAPI): GetContractorAllowedEntitiesResponse => ({
  allowedKycEntityTypes: allowed_kyc_entity_types,
});

export const transformContractOutstandingFeeFromAPI = ({
  id,
  contract_id,
  type,
  amount,
  vat_rate,
  currency,
  month,
  status,
}: ContractOutstandingFeeApi): ContractOutstandingFee => ({
  id,
  contractId: contract_id,
  type,
  amount,
  vatRate: vat_rate,
  currency,
  month,
  status,
});

export const transformUpdateContractorContractRequestToAPI = ({
  expectedInvoicingFrequency,
  rate,
  startDate,
  endDate,
}: UpdateContractorContractRequest): UpdateContractorContractRequestAPI => ({
  expected_invoicing_frequency: expectedInvoicingFrequency,
  rate,
  start_date: startDate,
  end_date: endDate,
});
